import moment from "moment";
import type { SignalsTemporalLabels } from "components/Signals/SignalsModels";
import type { SignalReportDatum } from "models/Reporting/ReportDatum";
import { getFirstDayOfQuarter } from "utils/DateTimeUtils";

const DATE_PARSE_FORMATS = [moment.ISO_8601, "MMM D, YYYY", "MMM DD, YYYY"];
const MONTH_PARSE_FORMATS = [
    moment.ISO_8601,
    "MMM YYYY",
    "MMM D, YYYY",
    "MMM DD, YYYY",
];

const parseTemporalXValue = (
    x: unknown,
    tempGrain: SignalsTemporalLabels,
): moment.Moment => {
    if (moment.isMoment(x)) {
        return x.clone();
    }

    if (x instanceof Date) {
        return moment(x);
    }

    if (typeof x === "string") {
        if (tempGrain === "Quarterly") {
            return getFirstDayOfQuarter(x);
        }

        const parsed = moment(
            x,
            tempGrain === "Monthly" ? MONTH_PARSE_FORMATS : DATE_PARSE_FORMATS,
            true,
        );

        if (parsed.isValid()) {
            return tempGrain === "Monthly" ? parsed.startOf("month") : parsed;
        }
    }

    return moment(x as moment.MomentInput);
};

/**
 * The label a chart gives one date bucket: the span it covers, clamped to the chart's own date
 * range so the partial first and last buckets read as the partial spans they are.
 *
 * This is the platform's date-span vocabulary. Tooltips, point-menu headers, drilldown banners and
 * date-grouped bars all write a bucket this way, so they agree on what a bucket is called.
 */
export const getXDateRangeLabel = (
    x: unknown,
    tempGrain: SignalsTemporalLabels,
    startDate: moment.Moment | undefined,
    endDate: moment.Moment | undefined,
    momentFormatString: string = "MMM D, YYYY",
): string => {
    const startDateCopy = moment(startDate);
    const endDateCopy = moment(endDate);
    const xMoment = parseTemporalXValue(x, tempGrain);

    switch (tempGrain) {
        case "Daily":
            return xMoment.format(momentFormatString);
        case "Weekly":
            const firstDayWeekly =
                xMoment < startDateCopy ? startDateCopy : xMoment;
            const lastDayW = xMoment.clone().add(6, "days");
            const lastDayToUse =
                lastDayW > endDateCopy ? endDateCopy : lastDayW;

            if (firstDayWeekly.isSame(lastDayToUse, "d")) {
                return firstDayWeekly.format(momentFormatString);
            }
            return `${firstDayWeekly.format(
                momentFormatString,
            )} - ${lastDayToUse.format(momentFormatString)}`;
        case "Monthly":
            const firstDayM = xMoment < startDateCopy ? startDateCopy : xMoment;
            const lastDayM = moment(xMoment).add(1, "month").subtract(1, "day");
            const lastDayToUseM =
                lastDayM > endDateCopy ? endDateCopy : lastDayM;
            if (firstDayM.isSame(lastDayToUseM, "d")) {
                return firstDayM.format(momentFormatString);
            }
            return `${firstDayM.format(
                momentFormatString,
            )} - ${lastDayToUseM.format(momentFormatString)}`;
        case "Quarterly":
            const firstDayOfQuarter = getFirstDayOfQuarter(
                xMoment.format("[Q]Q YYYY"),
            );
            const firstDayQ =
                firstDayOfQuarter < startDateCopy
                    ? startDateCopy
                    : firstDayOfQuarter;

            const lastDayQ = firstDayOfQuarter
                .clone()
                .add(1, "quarter")
                .subtract(1, "day");

            const lastDayToUseQ =
                lastDayQ > endDateCopy ? endDateCopy : lastDayQ;

            return `${firstDayQ.format(
                momentFormatString,
            )} - ${lastDayToUseQ.format(momentFormatString)}`;
    }
};

/**
 * A monthly bucket's axis label on a compact chart, where `May 1 - May 31` wraps onto several
 * lines. A whole month is named by its month alone, and a month the chart's range cuts short by
 * its days (`May 15\u201331`). Tooltips and drilldowns keep the full span from
 * `getXDateRangeLabel`.
 */
export const getCompactMonthLabel = (
    x: unknown,
    startDate: moment.Moment | undefined,
    endDate: moment.Moment | undefined,
): string => {
    const monthStart = parseTemporalXValue(x, "Monthly");
    const monthEnd = monthStart.clone().endOf("month");
    const firstDay =
        startDate && monthStart < startDate ? moment(startDate) : monthStart;
    const lastDay = endDate && monthEnd > endDate ? moment(endDate) : monthEnd;

    if (firstDay.isSame(lastDay, "d")) return firstDay.format("MMM D");
    if (firstDay.isSame(monthStart, "d") && lastDay.isSame(monthEnd, "d")) {
        return monthStart.format("MMM");
    }
    return `${firstDay.format("MMM D")}\u2013${lastDay.format("D")}`;
};

/**
 * How the dates inside a bucket label are written.
 *
 * A chart whose range sits inside one year does not repeat that year on every bucket; one that
 * crosses a year boundary has to, or two buckets a year apart read identically. This is the rule
 * the over-time charts already apply, see `AcxLineGraph` and `DataGridBuilders`, restated here so a
 * date-grouped chart writes its buckets the same way.
 */
export const dateBucketMomentFormat = (
    startDate?: moment.Moment,
    endDate?: moment.Moment,
): string =>
    startDate?.isValid() &&
    endDate?.isValid() &&
    startDate.year() === endDate.year()
        ? "MMM D"
        : "MMM D, YYYY";

/**
 * Rewrites each date bucket's label to the one an over-time chart would give the same bucket.
 *
 * The backend labels a bucket by the date it starts on (`Jan 04, 2026`, `Jan 2026`, `Q1 2026`),
 * which is the vocabulary the SQL grain expressions produce. An over-time chart labels a bucket by
 * the span it covers, so a weekly bucket reads `Jan 4 - Jan 10`. A chart grouped by date divides
 * time exactly as an over-time chart does, so it says so the same way.
 *
 * Only `grouping` is rewritten. `grouping_identifier` stays the bucket's ISO start date, which is
 * what ordering compares and what drilldown turns back into a date range. Neither can read a span
 * label, and both would break if the identifier moved with the text.
 *
 * A row whose identifier is not a date keeps the backend's label, which at least describes the row.
 */
export const relabelDateGroupingBuckets = (
    data: SignalReportDatum[],
    grain: SignalsTemporalLabels,
    startDate?: moment.Moment,
    endDate?: moment.Moment,
): SignalReportDatum[] => {
    if (!data.length) return data;

    const momentFormatString = dateBucketMomentFormat(startDate, endDate);
    // Bucket starts repeat across every series on the chart, so each distinct one is formatted once.
    const labelsByBucketStart = new Map<string, string>();

    return data.map((datum) => {
        const bucketStart = datum.grouping_identifier;
        if (!bucketStart) return datum;

        let label = labelsByBucketStart.get(bucketStart);
        if (label === undefined) {
            if (!moment(bucketStart, moment.ISO_8601, true).isValid()) {
                return datum;
            }

            label = getXDateRangeLabel(
                bucketStart,
                grain,
                startDate,
                endDate,
                momentFormatString,
            );
            labelsByBucketStart.set(bucketStart, label);
        }

        return { ...datum, grouping: label };
    });
};
